import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { getEstadoCaja, listAperturasActivas } from '../../services/data';
import { toMoney } from '../../utils/numbers';
import { nombreTurno } from '../../utils/turnos';
import { formatTime } from '../../utils/dates';
import AperturaTurno from './AperturaTurno';
import CuadresRecientes from './CuadresRecientes';
import './styles/cuadre-turno.css';

const fechaCorta = (ymd) => (ymd ? String(ymd).slice(0, 10).split('-').reverse().join('/') : '—');

// Estado operativo de un turno (sin montos: el cajero no ve valores administrativos).
const textoEstado = (t) => ({
  correccion: `Cierre habilitado para corrección (${fechaCorta(t.correccion?.fecha)})`,
  abierta: `Caja abierta desde ${formatTime(t.apertura?.inicioAt)}`,
  otro_cajero: `Caja abierta por ${t.apertura?.username || 'otro cajero'}`,
  cerrado: 'Turno de hoy ya cerrado',
  otro_turno_abierto: `Sin apertura · la caja de ${nombreTurno(t.otroTurnoAbierto?.turnoId)} está abierta`,
  sin_apertura: 'Sin apertura',
}[t.estado]);

// Caja del cajero. La identidad de una caja es sucursal + fecha + turno:
//  - Día y Noche: primero se elige el turno (no se asume) y se entra a la caja, corrección o apertura de ESE turno.
//  - Día completo: sin selector; el mismo flujo con su único turno.
//  - Fecha anterior sin cuadre: se puede abrir (una a la vez) aunque hoy ya tenga caja abierta o cerrada.
// El turno elegido viaja en el estado de la navegación: no se guarda como preferencia.
// Con state.resumen se muestra el resumen de la caja abierta (sin cerrarla ni cambiar nada).
// Debajo del flujo principal: los cuadres recientes de la sucursal (últimos 30 días).
export default function Caja() {
  return <>
    <CajaPrincipal />
    <div className="ct-shell ct-shell--recientes"><CuadresRecientes /></div>
  </>;
}

function CajaPrincipal() {
  const location = useLocation();
  const navigate = useNavigate();
  const resumen = !!location.state?.resumen;
  const turnoSel = location.state?.turnoId || '';
  const [estado, setEstado] = useState({ cargando: true, caja: null, abiertas: [], error: '' });
  useEffect(() => {
    let vivo = true;
    setEstado((e) => ({ ...e, cargando: true }));
    Promise.all([getEstadoCaja(), listAperturasActivas()])
      .then(([caja, abiertas]) => { if (vivo) setEstado({ cargando: false, caja, abiertas, error: '' }); })
      .catch((e) => { if (vivo) setEstado({ cargando: false, caja: null, abiertas: [], error: e.message || 'No se pudo consultar la caja' }); });
    return () => { vivo = false; };
  }, [location.key]);
  if (estado.cargando) return <div className="ct-shell">Cargando caja…</div>;
  if (estado.error) return <div className="ct-shell" role="alert">{estado.error}</div>;

  if (resumen) {
    const a = estado.abiertas.find((x) => x.id === location.state?.aperturaId) || (estado.abiertas.length === 1 ? estado.abiertas[0] : null);
    if (!a) return <Navigate to="/Finanzas/Caja" replace />;
    return (
      <div className="ct-shell">
        <section className="ct-card ct-vacio">
          <h2>Caja abierta</h2>
          <dl className="ct-apertura-datos" aria-label="Datos de apertura">
            <div><dt>Turno</dt><dd>{nombreTurno(a.turnoId)}</dd></div>
            <div><dt>Apertura</dt><dd>{fechaCorta(a.fecha)} · {a.inicioAt ? formatTime(a.inicioAt) : '—'}</dd></div>
            <div><dt>Efectivo inicial</dt><dd>{a.efectivoInicial != null ? toMoney(a.efectivoInicial) : '—'}</dd></div>
          </dl>
          <p>La caja sigue abierta. Puedes continuar con el cuadre cuando quieras.</p>
          <button type="button" className="ct-btn ct-btn--primary" onClick={() => navigate(`/Finanzas/RegistrarCierre?apertura=${encodeURIComponent(a.id)}`, { replace: true })}>Continuar con la caja</button>
        </section>
      </div>
    );
  }

  const { modalidad, turnos, anteriorAbierta } = estado.caja;
  const conSelector = modalidad === 'turnos';
  const t = conSelector ? turnos.find((x) => x.turnoId === turnoSel) : turnos[0];
  const elegir = (turnoId) => navigate('/Finanzas/Caja', { state: { turnoId } });
  const cambiar = conSelector && <button type="button" className="ct-btn" onClick={() => navigate('/Finanzas/Caja', { replace: true })}>Cambiar turno</button>;
  const irCaja = (a) => <Navigate to={`/Finanzas/RegistrarCierre?apertura=${encodeURIComponent(a.id)}`} replace />;
  // Quien abrió una fecha anterior la termina primero; después vuelve al flujo normal.
  if (anteriorAbierta?.propia) return irCaja(anteriorAbierta);

  if (conSelector && !t) {
    return (
      <div className="ct-shell">
        <section className="ct-card ct-vacio">
          <h2>¿Qué turno vas a trabajar?</h2>
          <p>Selecciona el turno</p>
          <div className="caja-turnos">
            {turnos.map((x) => <button key={x.turnoId} type="button" className="caja-turno" onClick={() => elegir(x.turnoId)}>
              <b>{nombreTurno(x.turnoId)}</b><small>{textoEstado(x)}</small></button>)}
          </div>
        </section>
      </div>
    );
  }

  const registrarAnterior = !anteriorAbierta && <button type="button" className="ct-btn" onClick={() => navigate('/Finanzas/Caja', { state: { turnoId: t.turnoId, anterior: true } })}>Registrar un día anterior</button>;
  const avisoAnterior = anteriorAbierta && <p>La caja del {fechaCorta(anteriorAbierta.fecha)} está abierta por {anteriorAbierta.username}. Solo se puede registrar un día anterior a la vez.</p>;
  // Una fecha anterior que quedó sin cuadre se puede abrir aunque hoy ya tenga caja abierta o cerrada.
  if (location.state?.anterior && !anteriorAbierta && t.estado !== 'sin_apertura') {
    return <>
      <div className="ct-shell" style={{ paddingBottom: 0 }}><div className="caja-acciones">
        <button type="button" className="ct-btn" onClick={() => navigate('/Finanzas/Caja', { replace: true, state: { turnoId: t.turnoId } })}>Volver</button>
      </div></div>
      <AperturaTurno turnoFijo={conSelector ? t.turnoId : ''} soloAnterior />
    </>;
  }
  if (t.estado === 'correccion') {
    return (
      <div className="ct-shell">
        <section className="ct-card ct-vacio">
          <h2>Cierre {nombreTurno(t.turnoId)} habilitado para corrección</h2>
          <p>Del {fechaCorta(t.correccion.fecha)} · turno {nombreTurno(t.turnoId)}. Corrígelo y vuelve a cerrarlo.</p>
          <div className="caja-acciones">
            <button type="button" className="ct-btn ct-btn--primary" onClick={() => navigate(`/Finanzas/RegistrarCierre?id=${encodeURIComponent(t.correccion.cierreId)}`)}>Corregir cierre</button>
            {t.apertura?.propia && <button type="button" className="ct-btn" onClick={() => navigate(`/Finanzas/RegistrarCierre?apertura=${encodeURIComponent(t.apertura.id)}`)}>Continuar con la caja abierta</button>}
            {registrarAnterior}
            {cambiar}
          </div>
        </section>
      </div>
    );
  }
  if (t.estado === 'abierta') return irCaja(t.apertura);
  if (['otro_cajero', 'cerrado', 'otro_turno_abierto'].includes(t.estado)) {
    return (
      <div className="ct-shell">
        <section className="ct-card ct-vacio">
          <h2>{t.estado === 'cerrado' ? `El turno ${nombreTurno(t.turnoId)} de hoy ya fue cerrado`
            : t.estado === 'otro_turno_abierto' ? `No se puede abrir el turno ${nombreTurno(t.turnoId)} todavía` : `El turno ${nombreTurno(t.turnoId)} ya tiene caja abierta`}</h2>
          <p>{t.estado === 'otro_turno_abierto'
            ? `La caja del turno ${nombreTurno(t.otroTurnoAbierto?.turnoId)} está abierta desde ${formatTime(t.otroTurnoAbierto?.inicioAt)}. La sucursal trabaja con una caja abierta a la vez: ciérrala antes de abrir ${nombreTurno(t.turnoId)}.`
            : t.estado === 'cerrado'
            ? 'No se puede abrir otra caja del mismo turno. Si hay que corregir el cierre, pide al administrador que lo habilite para corrección. Si un día anterior quedó sin cuadre, puedes registrarlo.'
            : `La abrió ${t.apertura?.username || 'otro cajero'} a las ${formatTime(t.apertura?.inicioAt)}. Solo quien la abrió puede continuarla.`}</p>
          {avisoAnterior}
          <div className="caja-acciones">
            {registrarAnterior}
            {cambiar}
          </div>
        </section>
      </div>
    );
  }
  // Sin apertura: abrir la caja de ese turno.
  return <>
    {conSelector && <div className="ct-shell" style={{ paddingBottom: 0 }}><div className="caja-acciones">{cambiar}</div></div>}
    <AperturaTurno turnoFijo={conSelector ? t.turnoId : ''} />
  </>;
}
